/**
 * Importação por CSV (roadmap #4): parser, sugestão de mapeamento e conversão
 * de linha em lead. Sem React nem Firestore, para ser testável sozinho.
 */
import { LEAD_ORIGINS, STAGES } from './constantes';
import { normalizeInstagram, normalizeUrl } from './formato';

export const CAMPOS = [
  { key: 'company_name',      label: 'Empresa *',          pistas: ['empresa', 'company', 'cliente', 'razao', 'nome fantasia', 'organizacao'] },
  { key: 'contact_name',      label: 'Contato',            pistas: ['contato', 'contact', 'nome', 'responsavel', 'pessoa'] },
  { key: 'contact_phone',     label: 'Telefone',           pistas: ['telefone', 'phone', 'whats', 'celular', 'fone', 'tel'] },
  { key: 'contact_email',     label: 'E-mail',             pistas: ['email', 'e-mail', 'mail'] },
  { key: 'site',              label: 'Site',               pistas: ['site', 'dominio'] },
  { key: 'instagram',         label: 'Instagram',          pistas: ['instagram'] },
  { key: 'stage',             label: 'Etapa',              pistas: ['etapa', 'stage', 'fase', 'status'] },
  { key: 'value',             label: 'Valor mensal',       pistas: ['valor', 'value', 'ticket', 'mensal', 'preco', 'r$'] },
  { key: 'origem',            label: 'Origem',             pistas: ['origem', 'source', 'fonte', 'canal'] },
  { key: 'seller',            label: 'Vendedor',           pistas: ['vendedor', 'seller', 'responsavel comercial', 'owner'] },
  { key: 'service_type',      label: 'Serviço',            pistas: ['servico', 'service', 'produto'] },
  { key: 'next_contact_date', label: 'Próxima abordagem',  pistas: ['proxima', 'abordagem', 'follow', 'retorno', 'next'] },
  { key: 'notes',             label: 'Observações',        pistas: ['observ', 'notes', 'nota', 'comentario', 'descricao', 'obs'] },
];

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/** Lê CSV com ; , ou tab, aspas com "" e quebras dentro de aspas. */
export function parseCSV(texto) {
  const t = String(texto || '').replace(/^﻿/, '');
  if (!t.trim()) return { colunas: [], linhas: [], delimitador: ';' };
  const primeira = t.split(/\r?\n/)[0];
  const conta = (d) => (primeira.match(new RegExp('\\' + d, 'g')) || []).length;
  const delimitador = [';', ',', '\t'].sort((a, b) => conta(b) - conta(a))[0];

  const registros = []; let campo = '', registro = [], aspas = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (aspas) {
      if (ch === '"') { if (t[i + 1] === '"') { campo += '"'; i++; } else aspas = false; }
      else campo += ch;
    } else if (ch === '"') aspas = true;
    else if (ch === delimitador) { registro.push(campo); campo = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++;
      registro.push(campo); registros.push(registro); registro = []; campo = '';
    } else campo += ch;
  }
  if (campo !== '' || registro.length) { registro.push(campo); registros.push(registro); }

  const naoVazio = registros.filter(r => r.some(c => String(c).trim() !== ''));
  const colunas = (naoVazio.shift() || []).map(c => String(c).trim());
  const linhas = naoVazio.map(r => Object.fromEntries(colunas.map((c, i) => [c, String(r[i] ?? '').trim()])));
  return { colunas, linhas, delimitador };
}

/** Coluna do CSV → campo do lead, pelo nome. Cada campo recebe no máximo uma coluna. */
export function sugerirMapeamento(colunas) {
  const mapa = {}; const usadas = new Set();
  for (const campo of CAMPOS) {
    const col = colunas.find(c => !usadas.has(c) && campo.pistas.some(p => norm(c).includes(p)));
    if (col) { mapa[campo.key] = col; usadas.add(col); }
  }
  // "nome" sozinho é contato, mas se não houver empresa mapeada vira empresa
  if (!mapa.company_name && mapa.contact_name && /^nome$/.test(norm(mapa.contact_name))) {
    mapa.company_name = mapa.contact_name; delete mapa.contact_name;
  }
  return mapa;
}

/** "R$ 1.500,00" → 1500 · "1500" → 1500 · "2,5" → 2.5 · "1,500.00" → 1500 */
export function parseValor(v) {
  let s = String(v ?? '').replace(/[^\d,.-]/g, '');
  if (!s) return 0;
  const ultVirg = s.lastIndexOf(','), ultPonto = s.lastIndexOf('.');
  if (ultVirg > -1 && ultPonto > -1) s = ultVirg > ultPonto ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  else if (ultVirg > -1) s = s.replace(/\./g, '').replace(',', '.');
  else if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, '');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

/** "31/12/2026" ou "2026-12-31" → "2026-12-31"; outra coisa → null */
export function parseData(v) {
  const s = String(v || '').trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return null;
}

function acharEtapa(v) {
  const n = norm(v); if (!n) return 'backlog';
  return STAGES.find(s => s.key === n || norm(s.label).replace(/[✓✗]/g, '').trim() === n || norm(s.label).startsWith(n))?.key || null;
}
function acharOrigem(v) {
  const n = norm(v); if (!n) return null;
  return LEAD_ORIGINS.find(o => o.key === n || norm(o.label) === n || norm(o.label).includes(n))?.key || null;
}

/**
 * Converte uma linha do CSV num lead pronto para gravar.
 * Devolve { lead, avisos, erro, duplicado }. `existentes` é um Set de nomes normalizados.
 */
export function linhaParaLead(linha, mapa, { sellers = [], existentes = new Set() } = {}) {
  const v = (k) => (mapa[k] ? linha[mapa[k]] : '') || '';
  const avisos = [];
  const company_name = v('company_name').trim();
  if (!company_name) return { lead: null, avisos, erro: 'Sem empresa', duplicado: false };

  let stage = 'backlog';
  if (v('stage')) { const e = acharEtapa(v('stage')); if (e) stage = e; else avisos.push(`etapa "${v('stage')}" desconhecida, vai para Backlog`); }

  let origem = null;
  if (v('origem')) { origem = acharOrigem(v('origem')); if (!origem) avisos.push(`origem "${v('origem')}" desconhecida`); }

  let seller_id = null, seller_name = '';
  if (v('seller')) {
    const s = sellers.find(x => norm(x.name) === norm(v('seller')) || norm(x.name).includes(norm(v('seller'))));
    if (s) { seller_id = s.id; seller_name = s.name; } else avisos.push(`vendedor "${v('seller')}" não cadastrado`);
  }

  let next_contact_date = null;
  if (v('next_contact_date')) { next_contact_date = parseData(v('next_contact_date')); if (!next_contact_date) avisos.push(`data "${v('next_contact_date')}" inválida`); }

  const lead = {
    company_name,
    contact_name: v('contact_name').trim(),
    contact_phone: v('contact_phone').trim(),
    contact_email: v('contact_email').trim().toLowerCase(),
    site: normalizeUrl(v('site')),
    instagram: normalizeInstagram(v('instagram')),
    stage, value: parseValor(v('value')), origem,
    seller_id, seller_name,
    service_type: v('service_type').trim() || null,
    next_contact_date,
    notes: v('notes').trim(),
    loss_reason: '', recurrence: null, variable_rate: 0, proposal_url: null,
  };
  return { lead, avisos, erro: null, duplicado: existentes.has(norm(company_name)) };
}

/** Prepara o lote inteiro, com contagens para a prévia. */
export function prepararLote(linhas, mapa, ctx) {
  const itens = linhas.map((linha, i) => ({ n: i + 1, ...linhaParaLead(linha, mapa, ctx) }));
  return {
    itens,
    validos: itens.filter(x => !x.erro).length,
    erros: itens.filter(x => x.erro).length,
    duplicados: itens.filter(x => !x.erro && x.duplicado).length,
    comAviso: itens.filter(x => !x.erro && x.avisos.length).length,
  };
}
